import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, MapPin, Utensils, Clock, ArrowRight, Navigation, Download, Sparkles } from "lucide-react";
import { getSharedProItinerary } from "@/lib/pro/pipeline.functions";
import { exportProPlanPdf } from "@/lib/pro/pdf-export";
import type { ProPlan } from "@/lib/pro/pipeline.functions";
import { NavigationReadinessBar } from "@/components/NavigationReadinessBar";

export const Route = createFileRoute("/pro/r/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `Roteiro Pro partilhado — Routify` },
      { name: "description", content: `Roteiro Routify Pro (${params.slug}) — planeamento multi-dia partilhado.` },
    ],
  }),
  component: SharedProPage,
});

const TRANSPORT_LABEL: Record<string, string> = { walk: "A pé", bike: "Bicicleta", car: "Carro", transit: "Transportes públicos" };
const PACE_LABEL: Record<string, string> = { slow: "Calmo", normal: "Normal", intense: "Intenso" };

function mapsUrlFor(s: { name: string; lat?: number; lng?: number; placeId?: string }) {
  const params = new URLSearchParams({ api: "1", query: s.lat && s.lng ? `${s.lat},${s.lng}` : s.name });
  if (s.placeId) params.set("query_place_id", s.placeId);
  return `https://www.google.com/maps/search/?${params.toString()}`;
}
function navUrlFor(s: { name: string; lat?: number; lng?: number; placeId?: string }) {
  const params = new URLSearchParams({ api: "1", destination: s.lat && s.lng ? `${s.lat},${s.lng}` : s.name });
  if (s.placeId) params.set("destination_place_id", s.placeId);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function SharedProPage() {
  const { slug } = Route.useParams();
  const fetchFn = useServerFn(getSharedProItinerary);
  const [plan, setPlan] = useState<(ProPlan & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFn({ data: { slug } })
      .then((row) => setPlan({ ...(row.plan as ProPlan), id: row.id }))
      .catch(() => setError("Roteiro não encontrado ou já não está partilhado."))
      .finally(() => setLoading(false));
  }, [slug, fetchFn]);

  if (loading) {
    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-3xl items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (error || !plan) {
    return (
      <main className="mx-auto w-full max-w-2xl px-4 py-16 text-center">
        <p className="text-muted-foreground">{error ?? "Sem dados."}</p>
        <Link to="/" className="mt-4 inline-flex items-center gap-1 text-sm text-brand hover:underline">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Início
      </Link>

      <header className="mt-6 mb-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500/15 to-brand/15 px-3 py-1 text-xs font-semibold text-brand">
          <Sparkles className="h-3.5 w-3.5" /> Roteiro Pro partilhado
        </div>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">{plan.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {plan.days.length} dia{plan.days.length !== 1 && "s"} • Transporte: {TRANSPORT_LABEL[plan.transport] ?? plan.transport} • Ritmo: {PACE_LABEL[plan.pace] ?? plan.pace}
            </p>
          </div>
          <button
            onClick={() => exportProPlanPdf(plan)}
            className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
          >
            <Download className="h-4 w-4" /> Descarregar PDF
          </button>
        </div>
      </header>

      {plan.days.map((d) => (
        <section key={d.day} className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Dia {d.day} — {d.city}</h2>
          {d.intro && <p className="mt-1 text-sm text-muted-foreground">{d.intro}</p>}
          {d.stops.length > 0 && (() => {
            const first = d.stops.find((s) => typeof s.lat === "number" && typeof s.lng === "number");
            const routeStops = d.stops.flatMap((stop) =>
              typeof stop.lat === "number" && typeof stop.lng === "number"
                ? [{ lat: stop.lat, lng: stop.lng, name: stop.name }]
                : [],
            );
            return (
              <NavigationReadinessBar
                className="mt-3"
                startPoint={first ? { lat: first.lat as number, lng: first.lng as number } : null}
                startPointName={d.stops[0]?.name}
                routeStops={routeStops}
                mode={plan.transport}
              />
            );
          })()}
          <ol className="mt-3 space-y-3">
            {d.stops.map((s, i) => (
              <li key={i} className="flex gap-3 border-l-2 border-brand/40 pl-3">
                <div className="flex-1">
                  {s.travelFromPrev && (
                    <div className="mb-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      <ArrowRight className="h-3 w-3" />
                      {s.travelFromPrev.minutes} min {s.travelFromPrev.label} ({(s.travelFromPrev.meters / 1000).toFixed(1)} km)
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {s.kind === "meal" ? <Utensils className="h-4 w-4 text-amber-500" /> : <MapPin className="h-4 w-4 text-brand" />}
                    {s.kind === "poi" && (s.lat || s.placeId) ? (
                      <a href={mapsUrlFor(s)} target="_blank" rel="noopener noreferrer" className="hover:underline">{s.name}</a>
                    ) : (<span>{s.name}</span>)}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {s.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.time} • {s.durationMin} min</span>}
                    {s.kind === "poi" && (s.lat || s.placeId) && (
                      <a href={navUrlFor(s)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">
                        <Navigation className="h-3 w-3" /> Navegar
                      </a>
                    )}
                  </div>
                  {s.note && <p className="mt-1 text-sm text-muted-foreground">{s.note}</p>}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}

      <div className="mt-10 rounded-2xl border border-brand/20 bg-gradient-to-br from-amber-500/10 to-brand/10 p-6 text-center">
        <Sparkles className="mx-auto h-6 w-6 text-brand" />
        <h3 className="mt-2 text-lg font-bold">Gostas desta rota?</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Esta rota foi criada com <strong>Routify Pro</strong>. Podes vê-la e navegar pelas paragens à vontade.
          Para criares as tuas próprias rotas multi-cidade otimizadas, precisas do Pro.
        </p>
        <Link
          to="/pro"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand/90"
        >
          <Sparkles className="h-4 w-4" /> Criar a minha rota Pro
        </Link>
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Criado com <Link to="/" className="text-brand hover:underline">Routify Pro</Link>
      </p>
    </main>
  );
}
